import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, FileTextIcon, UserIcon, HomeIcon, CalculatorIcon, CheckCircleIcon, ClockIcon, DownloadIcon, PrinterIcon, EditIcon, SaveIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import type { Contract } from '../../contexts/RentalContext';
import { useRental } from '../../contexts/RentalContext';
import { toast } from 'sonner';
import { ContractPreviewModal } from './ContractPreviewModal';
interface ContractDetailsDrawerProps {
  contract: Contract | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ContractDetailsDrawer({
  contract,
  isOpen,
  onClose
}: ContractDetailsDrawerProps) {
  const {
    updateContract
  } = useRental();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [editedData, setEditedData] = useState<Partial<Contract>>({});
  if (!contract) return null;
  // Initialize edited data when entering edit mode
  const handleEnterEditMode = () => {
    setEditedData({
      clientName: contract.clientName,
      clientPhone: contract.clientPhone,
      clientEmail: contract.clientEmail,
      clientCNP: contract.clientCNP,
      clientCI: contract.clientCI,
      clientAddress: contract.clientAddress,
      clientCUI: contract.clientCUI,
      clientCompanyName: contract.clientCompanyName,
      clientCompanyAddress: contract.clientCompanyAddress,
      clientRegCom: contract.clientRegCom,
      clientLegalRep: contract.clientLegalRep,
      propertyAddress: contract.propertyAddress,
      propertyType: contract.propertyType,
      rentalPrice: contract.rentalPrice,
      commissionPercent: contract.commissionPercent,
      vatIncluded: contract.vatIncluded
    });
    setIsEditMode(true);
  };
  const handleSaveChanges = () => {
    if (contract) {
      updateContract(contract.id, editedData);
      setIsEditMode(false);
      toast.success('Modificări salvate cu succes!');
    }
  };
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData({});
  };
  const updateEditedField = (field: keyof Contract, value: any) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  // Get current data (edited or original)
  const currentData = isEditMode ? {
    ...contract,
    ...editedData
  } : contract;
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, {
      label: string;
      color: string;
      icon: any;
    }> = {
      draft: {
        label: 'Draft',
        color: 'bg-slate-100 text-slate-700',
        icon: FileTextIcon
      },
      sent: {
        label: 'Trimis spre semnare',
        color: 'bg-blue-100 text-blue-700',
        icon: ClockIcon
      },
      signed: {
        label: 'Contract semnat',
        color: 'bg-emerald-100 text-emerald-700',
        icon: CheckCircleIcon
      },
      printed: {
        label: 'De semnat fizic',
        color: 'bg-orange-100 text-orange-700',
        icon: DownloadIcon
      }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return <span className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-bold ${config.color}`} data-id="element-3115">
        <Icon className="w-4 h-4" data-id="element-3116" />
        <span data-id="element-3117">{config.label}</span>
      </span>;
  };
  const getContractText = () => {
    const today = new Date(contract.createdAt).toLocaleDateString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    if (contract.type === 'tenant') {
      return `CONTRACT DE INTERMEDIERE ÎN VEDEREA ÎNCHIRIERII

ÎNCHEIAT ASTĂZI, ${today}

ÎNTRE:

1. FREIM ESTATE SRL, cu sediul social în București, Str. Mareșal Alexandru Averescu, nr. 15b-15c, Etaj 4, Sector 1, înregistrată la Registrul Comerțului sub nr. J40/4535/14.04.2020, având CUI 42451855, denumit în continuare Intermediar, pe de-o parte;

ȘI

2. ${contract.clientType === 'PF' ? `${contract.clientName}, domiciliat în ${contract.clientAddress}, identificat cu CI seria ${contract.clientCI}, CNP ${contract.clientCNP}` : `${contract.clientCompanyName}, având sediul social în ${contract.clientCompanyAddress}, Nr. Reg. Com.: ${contract.clientRegCom}, CUI: ${contract.clientCUI}, reprezentată de ${contract.clientLegalRep}`}, denumit în continuare Chiriaș, pe de altă parte

ART. 1 - OBIECTUL CONTRACTULUI

Obiectul prezentului contract îl constituie prestarea de către Intermediar a serviciilor de intermediere imobiliară pentru facilitarea vizionării și eventual închirierii unui imobil identificat de Intermediar.

Imobilul identificat este situat la adresa: ${contract.propertyAddress}

ART. 2 - COMISIONUL

Comisionul datorat de către Chiriaș către Intermediar este de ${contract.commissionPercent}% din valoarea primei luni de chirie ${contract.vatIncluded ? '(TVA inclus)' : '+ TVA'}.

[... text complet contract ...]`;
    } else {
      return `CONTRACT DE REZERVARE IMOBIL-ÎNCHIRIERE

Nr. ${contract.id} / Data: ${today}

I. Părțile contractante

1. PROPRIETAR: ${contract.clientType === 'PF' ? `${contract.clientName}, cu domiciliul în ${contract.clientAddress}, CNP ${contract.clientCNP}` : `${contract.clientCompanyName}, cu sediul în ${contract.clientCompanyAddress}, CUI ${contract.clientCUI}, reprezentat de ${contract.clientLegalRep}`}

2. CHIRIAȘ: [Va fi completat la semnare]

II. Obiectul contractului

Obiectul prezentului contract îl constituie rezervarea imobilului situat în ${contract.propertyAddress}.

[... text complet contract ...]`;
    }
  };
  return <AnimatePresence data-id="element-3118">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black bg-opacity-50 z-40" data-id="element-3119" />

          {/* Drawer */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl z-50 overflow-y-auto" data-id="element-3120">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-[#132C53] to-[#1e4278] text-white p-6 z-10" data-id="element-3121">
              <div className="flex items-center justify-between mb-4" data-id="element-3122">
                <div data-id="element-3123">
                  <h2 className="text-2xl font-bold" data-id="element-3124">Detalii Contract</h2>
                  <p className="text-gray-300 text-sm mt-1" data-id="element-3125">{contract.id}</p>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors" data-id="element-3126">
                  <XIcon className="w-6 h-6" data-id="element-3127" />
                </button>
              </div>

              {/* Status Badge & Actions */}
              <div className="flex items-center justify-between" data-id="element-3128">
                {getStatusBadge(contract.status)}
                <div className="flex items-center space-x-2" data-id="element-3129">
                  {!isEditMode ? <>
                      <button onClick={handleEnterEditMode} className="flex items-center space-x-2 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-colors text-sm font-semibold" data-id="element-3130">
                        <EditIcon className="w-4 h-4" data-id="element-3131" />
                        <span data-id="element-3132">Editează</span>
                      </button>
                      <button onClick={() => setIsPreviewModalOpen(true)} className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors" title="Vezi Contract Complet" data-id="element-3133">
                        <EyeIcon className="w-5 h-5" data-id="element-3134" />
                      </button>
                      <button className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors" data-id="element-3135">
                        <DownloadIcon className="w-5 h-5" data-id="element-3136" />
                      </button>
                      <button className="p-2 hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors" data-id="element-3137">
                        <PrinterIcon className="w-5 h-5" data-id="element-3138" />
                      </button>
                    </> : <>
                      <button onClick={handleSaveChanges} className="flex items-center space-x-2 px-3 py-2 bg-green-500 hover:bg-green-600 rounded-lg transition-colors text-sm font-semibold" data-id="element-3139">
                        <SaveIcon className="w-4 h-4" data-id="element-3140" />
                        <span data-id="element-3141">Salvează</span>
                      </button>
                      <button onClick={handleCancelEdit} className="flex items-center space-x-2 px-3 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-colors text-sm font-semibold" data-id="element-3142">
                        <XIcon className="w-4 h-4" data-id="element-3143" />
                        <span data-id="element-3144">Anulează</span>
                      </button>
                    </>}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6" data-id="element-3145">
              {/* Date Client */}
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-3146">
                <div className="flex items-center space-x-3 mb-4" data-id="element-3147">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-3148">
                    <UserIcon className="w-5 h-5 text-blue-600" data-id="element-3149" />
                  </div>
                  <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3150">
                    Date Client
                  </h3>
                  {isEditMode && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold" data-id="element-3151">
                      Mod Editare
                    </span>}
                </div>

                <div className="space-y-3" data-id="element-3152">
                  <div className="flex justify-between items-center" data-id="element-3153">
                    <span className="text-sm text-slate-600" data-id="element-3154">
                      Tip contract:
                    </span>
                    <span className="text-sm font-semibold text-slate-900" data-id="element-3155">
                      {contract.type === 'landlord' ? 'Proprietar' : 'Chiriaș'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3156">
                    <span className="text-sm text-slate-600" data-id="element-3157">
                      Tip persoană:
                    </span>
                    <span className="text-sm font-semibold text-slate-900" data-id="element-3158">
                      {contract.clientType === 'PF' ? 'Persoană Fizică' : 'Persoană Juridică'}
                    </span>
                  </div>

                  {/* Nume - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3159">
                    <span className="text-sm text-slate-600" data-id="element-3160">Nume:</span>
                    {isEditMode ? <input type="text" value={currentData.clientName} onChange={e => updateEditedField('clientName', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3161" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3162">
                        {currentData.clientName}
                      </span>}
                  </div>

                  {contract.clientType === 'PF' ? <>
                      {/* CNP - Editable */}
                      <div className="flex justify-between items-center" data-id="element-3163">
                        <span className="text-sm text-slate-600" data-id="element-3164">CNP:</span>
                        {isEditMode ? <input type="text" value={currentData.clientCNP} onChange={e => updateEditedField('clientCNP', e.target.value)} maxLength={13} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3165" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3166">
                            {currentData.clientCNP}
                          </span>}
                      </div>

                      {/* CI - Editable */}
                      <div className="flex justify-between items-center" data-id="element-3167">
                        <span className="text-sm text-slate-600" data-id="element-3168">CI:</span>
                        {isEditMode ? <input type="text" value={currentData.clientCI} onChange={e => updateEditedField('clientCI', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3169" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3170">
                            {currentData.clientCI}
                          </span>}
                      </div>

                      {/* Adresă - Editable */}
                      <div className="flex justify-between items-start" data-id="element-3171">
                        <span className="text-sm text-slate-600" data-id="element-3172">Adresă:</span>
                        {isEditMode ? <textarea value={currentData.clientAddress} onChange={e => updateEditedField('clientAddress', e.target.value)} rows={2} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right w-64" data-id="element-3173" /> : <span className="text-sm font-semibold text-slate-900 text-right" data-id="element-3174">
                            {currentData.clientAddress}
                          </span>}
                      </div>
                    </> : <>
                      {/* CUI - Editable */}
                      <div className="flex justify-between items-center" data-id="element-3175">
                        <span className="text-sm text-slate-600" data-id="element-3176">CUI:</span>
                        {isEditMode ? <input type="text" value={currentData.clientCUI} onChange={e => updateEditedField('clientCUI', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3177" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3178">
                            {currentData.clientCUI}
                          </span>}
                      </div>

                      {/* Reg. Com. - Editable */}
                      <div className="flex justify-between items-center" data-id="element-3179">
                        <span className="text-sm text-slate-600" data-id="element-3180">
                          Reg. Com.:
                        </span>
                        {isEditMode ? <input type="text" value={currentData.clientRegCom} onChange={e => updateEditedField('clientRegCom', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3181" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3182">
                            {currentData.clientRegCom}
                          </span>}
                      </div>

                      {/* Reprezentant - Editable */}
                      <div className="flex justify-between items-center" data-id="element-3183">
                        <span className="text-sm text-slate-600" data-id="element-3184">
                          Reprezentant:
                        </span>
                        {isEditMode ? <input type="text" value={currentData.clientLegalRep} onChange={e => updateEditedField('clientLegalRep', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3185" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3186">
                            {currentData.clientLegalRep}
                          </span>}
                      </div>

                      {/* Sediu - Editable */}
                      <div className="flex justify-between items-start" data-id="element-3187">
                        <span className="text-sm text-slate-600" data-id="element-3188">Sediu:</span>
                        {isEditMode ? <textarea value={currentData.clientCompanyAddress} onChange={e => updateEditedField('clientCompanyAddress', e.target.value)} rows={2} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right w-64" data-id="element-3189" /> : <span className="text-sm font-semibold text-slate-900 text-right" data-id="element-3190">
                            {currentData.clientCompanyAddress}
                          </span>}
                      </div>
                    </>}

                  {/* Telefon - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3191">
                    <span className="text-sm text-slate-600" data-id="element-3192">Telefon:</span>
                    {isEditMode ? <input type="tel" value={currentData.clientPhone} onChange={e => updateEditedField('clientPhone', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3193" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3194">
                        {currentData.clientPhone}
                      </span>}
                  </div>

                  {/* Email - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3195">
                    <span className="text-sm text-slate-600" data-id="element-3196">Email:</span>
                    {isEditMode ? <input type="email" value={currentData.clientEmail} onChange={e => updateEditedField('clientEmail', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-64" data-id="element-3197" /> : <span className="text-sm font-semibold text-slate-900" data-id="element-3198">
                        {currentData.clientEmail}
                      </span>}
                  </div>
                </div>
              </div>

              {/* Date Proprietate */}
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-3199">
                <div className="flex items-center space-x-3 mb-4" data-id="element-3200">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-3201">
                    <HomeIcon className="w-5 h-5 text-green-600" data-id="element-3202" />
                  </div>
                  <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3203">
                    Date Proprietate
                  </h3>
                  {isEditMode && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold" data-id="element-3204">
                      Mod Editare
                    </span>}
                </div>

                <div className="space-y-3" data-id="element-3205">
                  {/* Tip imobil - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3206">
                    <span className="text-sm text-slate-600" data-id="element-3207">Tip imobil:</span>
                    {isEditMode ? <select value={currentData.propertyType} onChange={e => updateEditedField('propertyType', e.target.value)} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 capitalize" data-id="element-3208">
                        <option value="apartament" data-id="element-3209">Apartament</option>
                        <option value="casa" data-id="element-3210">Casă</option>
                        <option value="vila" data-id="element-3211">Vilă</option>
                        <option value="spatiu_comercial" data-id="element-3212">
                          Spațiu comercial
                        </option>
                        <option value="birouri" data-id="element-3213">Birouri</option>
                      </select> : <span className="text-sm font-semibold text-slate-900 capitalize" data-id="element-3214">
                        {currentData.propertyType}
                      </span>}
                  </div>

                  {/* Adresă - Editable */}
                  <div className="flex justify-between items-start" data-id="element-3215">
                    <span className="text-sm text-slate-600" data-id="element-3216">Adresă:</span>
                    {isEditMode ? <textarea value={currentData.propertyAddress} onChange={e => updateEditedField('propertyAddress', e.target.value)} rows={2} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right w-64" data-id="element-3217" /> : <span className="text-sm font-semibold text-slate-900 text-right" data-id="element-3218">
                        {currentData.propertyAddress}
                      </span>}
                  </div>

                  {/* Preț chirie - Editable */}
                  {currentData.rentalPrice > 0 && <div className="flex justify-between items-center" data-id="element-3219">
                      <span className="text-sm text-slate-600" data-id="element-3220">
                        Preț chirie:
                      </span>
                      {isEditMode ? <div className="flex items-center space-x-2" data-id="element-3221">
                          <input type="number" value={currentData.rentalPrice} onChange={e => updateEditedField('rentalPrice', parseFloat(e.target.value))} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-24" data-id="element-3222" />
                          <span className="text-sm font-semibold text-slate-900" data-id="element-3223">
                            EUR
                          </span>
                        </div> : <span className="text-sm font-semibold text-slate-900" data-id="element-3224">
                          {currentData.rentalPrice} EUR
                        </span>}
                    </div>}
                </div>
              </div>

              {/* Comisionare */}
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-3225">
                <div className="flex items-center space-x-3 mb-4" data-id="element-3226">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-3227">
                    <CalculatorIcon className="w-5 h-5 text-purple-600" data-id="element-3228" />
                  </div>
                  <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3229">
                    Comisionare
                  </h3>
                  {isEditMode && <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-bold" data-id="element-3230">
                      Mod Editare
                    </span>}
                </div>

                <div className="space-y-3" data-id="element-3231">
                  {/* Procent - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3232">
                    <span className="text-sm text-slate-600" data-id="element-3233">Procent:</span>
                    {isEditMode ? <div className="flex items-center space-x-2" data-id="element-3234">
                        <input type="number" value={currentData.commissionPercent} onChange={e => updateEditedField('commissionPercent', parseFloat(e.target.value))} min="0" max="100" className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 w-20" data-id="element-3235" />
                        <span className="text-sm font-semibold text-slate-900" data-id="element-3236">
                          %
                        </span>
                      </div> : <span className="text-sm font-semibold text-slate-900" data-id="element-3237">
                        {currentData.commissionPercent}%
                      </span>}
                  </div>

                  {/* TVA - Editable */}
                  <div className="flex justify-between items-center" data-id="element-3238">
                    <span className="text-sm text-slate-600" data-id="element-3239">TVA:</span>
                    {isEditMode ? <select value={currentData.vatIncluded ? 'included' : 'plus'} onChange={e => updateEditedField('vatIncluded', e.target.value === 'included')} className="text-sm font-semibold text-slate-900 border border-blue-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3240">
                        <option value="included" data-id="element-3241">Inclus</option>
                        <option value="plus" data-id="element-3242">+ 21%</option>
                      </select> : <span className="text-sm font-semibold text-slate-900" data-id="element-3243">
                        {currentData.vatIncluded ? 'Inclus' : '+ 21%'}
                      </span>}
                  </div>
                </div>
              </div>

              {/* Preview Contract - Collapsed */}
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-3244">
                <div className="flex items-center justify-between mb-4" data-id="element-3245">
                  <div className="flex items-center space-x-3" data-id="element-3246">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-3247">
                      <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-3248" />
                    </div>
                    <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3249">
                      Preview Contract
                    </h3>
                  </div>
                  <button onClick={() => setIsPreviewModalOpen(true)} className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-semibold" data-id="element-3250">
                    <EyeIcon className="w-4 h-4" data-id="element-3251" />
                    <span data-id="element-3252">Vezi Complet</span>
                  </button>
                </div>

                <p className="text-sm text-slate-500 mb-4" data-id="element-3253">
                  Vizualizează întregul contract în format de printare.
                </p>

                <button onClick={() => setIsPreviewModalOpen(true)} className="w-full py-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-500 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2" data-id="element-3254">
                  <FileTextIcon className="w-5 h-5" data-id="element-3255" />
                  <span data-id="element-3256">Deschide Previzualizare</span>
                </button>
              </div>

              {/* Istoric Semnături */}
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-id="element-3257">
                <div className="flex items-center space-x-3 mb-4" data-id="element-3258">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center" data-id="element-3259">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-3260" />
                  </div>
                  <h3 className="text-lg font-bold text-[#132C53]" data-id="element-3261">
                    Istoric Semnături
                  </h3>
                </div>

                <div className="space-y-4" data-id="element-3262">
                  {/* Agent Signature */}
                  <div className="flex items-start space-x-3" data-id="element-3263">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${contract.agentSignature ? 'bg-emerald-100' : 'bg-slate-100'}`} data-id="element-3264">
                      {contract.agentSignature ? <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-3265" /> : <ClockIcon className="w-5 h-5 text-slate-400" data-id="element-3266" />}
                    </div>
                    <div className="flex-1" data-id="element-3267">
                      <p className="text-sm font-semibold text-slate-900" data-id="element-3268">
                        Semnătură Agent
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-3269">
                        {contract.agentSignature ? 'Semnat' : 'În așteptare'}
                      </p>
                      {contract.agentSignature && <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white" data-id="element-3270">
                          <img src={contract.agentSignature} alt="Agent Signature" className="w-32 h-16 object-contain" data-id="element-3271" />
                        </div>}
                    </div>
                  </div>

                  {/* Client Signature */}
                  <div className="flex items-start space-x-3" data-id="element-3272">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${contract.clientSignature ? 'bg-emerald-100' : 'bg-slate-100'}`} data-id="element-3273">
                      {contract.clientSignature ? <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-3274" /> : <ClockIcon className="w-5 h-5 text-slate-400" data-id="element-3275" />}
                    </div>
                    <div className="flex-1" data-id="element-3276">
                      <p className="text-sm font-semibold text-slate-900" data-id="element-3277">
                        Semnătură Client
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-3278">
                        {contract.clientSignature ? 'Semnat' : contract.status === 'sent' ? 'Trimis spre semnare' : contract.status === 'printed' ? 'De semnat fizic' : 'În așteptare'}
                      </p>
                      {contract.clientSignature && <div className="mt-2 border border-slate-200 rounded-lg p-2 bg-white" data-id="element-3279">
                          <img src={contract.clientSignature} alt="Client Signature" className="w-32 h-16 object-contain" data-id="element-3280" />
                        </div>}
                      {contract.signingLink && <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-3281">
                          <p className="text-xs text-gray-600 mb-1" data-id="element-3282">
                            Link semnare:
                          </p>
                          <p className="text-xs font-mono text-gray-800 break-all" data-id="element-3283">
                            {contract.signingLink}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* Signed Date */}
                  {contract.signedAt && <div className="pt-3 border-t border-slate-200" data-id="element-3284">
                      <p className="text-xs text-slate-500" data-id="element-3285">
                        Contract semnat la:{' '}
                        <span className="font-semibold text-slate-700" data-id="element-3286">
                          {new Date(contract.signedAt).toLocaleString('ro-RO')}
                        </span>
                      </p>
                    </div>}
                </div>
              </div>

              {/* Status Curent */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6" data-id="element-3287">
                <h3 className="text-lg font-bold text-[#132C53] mb-3" data-id="element-3288">
                  Status Curent
                </h3>
                <div className="flex items-center justify-between" data-id="element-3289">
                  {getStatusBadge(contract.status)}
                  <p className="text-xs text-slate-500" data-id="element-3290">
                    Creat la:{' '}
                    {new Date(contract.createdAt).toLocaleString('ro-RO')}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contract Preview Modal */}
          <ContractPreviewModal contract={currentData} isOpen={isPreviewModalOpen} onClose={() => setIsPreviewModalOpen(false)} data-id="element-3291" />
        </>}
    </AnimatePresence>;
}